import { useState } from "react";

import {
  format,
  addMonths,
  subMonths,
  setMonth,
  setYear,
  addYears,
  subYears,
} from "date-fns";

import { getCurrentLocale } from "@packages/shared/utils/getCurrentLocale";

import { Icon } from "@/components/Icon";
import { TabPanel } from "@/components/TabPanel";
import { Text } from "@/components/Text";

import { CalendarDays } from "./CalendarDays";
import { CalendarMonths } from "./CalendarMonths";
import { CalendarYears } from "./CalendarYears";

import styles from "./Calendar.module.scss";

interface CalendarProps {
  value: Date;
  onChange: (date: Date, shouldClose?: boolean) => void;
}

export const Calendar = ({
  value,
  onChange,
}: CalendarProps): React.JSX.Element | null => {
  const [currentTab, setCurrentTab] = useState(0);

  const handleDayChange = (day: Date) => {
    onChange(day, true);
  };
  const handleMonthChange = (month: number) => {
    onChange(setMonth(value, month), true);
  };
  const handleYearChange = (year: number) => {
    onChange(setYear(value, year), true);
  };

  const handleNextClick = () => {
    if (currentTab === 0) {
      return onChange(addMonths(value, 1));
    }

    if (currentTab === 1) {
      return onChange(addYears(value, 1));
    }

    return onChange(addYears(value, 10));
  };

  const handlePrevClick = () => {
    if (currentTab === 0) {
      return onChange(subMonths(value, 1));
    }

    if (currentTab === 1) {
      return onChange(subYears(value, 1));
    }

    return onChange(subYears(value, 10));
  };

  const handleModeChange = () => {
    setCurrentTab((p) => (p === 2 ? 0 : p + 1));
  };

  return (
    <div className={styles["container"]}>
      <div className={styles["header"]}>
        <button
          type="button"
          style={{
            transform: "rotate(180deg)",
          }}
          onClick={() => handlePrevClick()}
        >
          <Icon icon="arrow-right" stroke="currentColor" />
        </button>
        <Text
          variant="text-2-medium"
          containerClassName={styles["date-label"]}
          onClick={handleModeChange}
        >
          {format(value, "PP", { locale: getCurrentLocale() })}
        </Text>
        <button type="button" onClick={() => handleNextClick()}>
          <Icon icon="arrow-right" stroke="currentColor" />
        </button>
      </div>

      <TabPanel value={currentTab} tabValue={0}>
        <CalendarDays currentDate={value} onDayChange={handleDayChange} />
      </TabPanel>

      <TabPanel value={currentTab} tabValue={1}>
        <CalendarMonths currentDate={value} onMonthChange={handleMonthChange} />
      </TabPanel>

      <TabPanel value={currentTab} tabValue={2}>
        <CalendarYears currentDate={value} onYearChange={handleYearChange} />
      </TabPanel>
    </div>
  );
};
